import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card } from "react-bootstrap";
import MasterListPage from "./MasterListPage";
import ActionDropdown from "Common/ActionDropdown/ActionDropdown";
import { useDispatch, useSelector } from "react-redux";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import {
  deleteProductEnquiry,
  deleteServiceEnquiry,
} from "slices/enquiry/thunk";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { ENQ } from "Constants/StatusConstant";
import { showConfirm } from "helpers/swalHelper";
import Tooltip from "@mui/material/Tooltip";
import Chip from "components/Chips/Chip";
import TruncatedTextWithTooltip from "Common/Components/TruncatedTextWithTooltip";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";
interface EnquiryPageProps {
  type: "product" | "service";
  /** Tags-search: switch back to Enquiry Log without route change */
  onTagsSearchTabNavigate?: (tabValue: string) => void;
  noWrapper?: boolean;
  forceShowOnTagsSearch?: boolean;
}

const EnquiryDraftPage: React.FC<EnquiryPageProps> = ({
  type,
  onTagsSearchTabNavigate,
  noWrapper = false,
  forceShowOnTagsSearch = false,
}) => {
  document.title = type==="product" ? "Enquiry Product" : "Enquiry Service";
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [reload, setReload] = useState(false);

  const activeType = type; // product by default

  const addButtonLabel =
    activeType === "product"
      ? "Create Product Enquiry"
      : "Create Service Enquiry";

  const addRoute =
    activeType === "product"
      ? `/enquiry/add-product-enquiry`
      : `/enquiry/add-services-enquiry`;

 
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.enquiry?.app_name,
    activeType === "product" ? app_modules?.enquiry?.modules?.product : app_modules?.enquiry?.modules?.service
  );

  const handleEdit = (enquiryId: string) => {
    navigate(`/enquiry/${activeType}/edit/${enquiryId}`);
  };

  const handleDelete = async (enquiryId: string) => {
    const result = await showConfirm(
      "This enquiry will be permanently deleted. Do you want to continue?",
      "Delete Enquiry",
      "Yes, delete",
      "Cancel"
    );

    if (!result.isConfirmed) return;
    try {
      if (activeType === "product") {
        await dispatch(deleteProductEnquiry(enquiryId)).unwrap();
      } else {
        await dispatch(deleteServiceEnquiry(enquiryId)).unwrap();
      }

      toast.success("Enquiry deleted successfully");
      setReload((prev) => !prev); // ✅ trigger re-fetch
    } catch (error: any) {
      toast.error(error || "Failed to delete enquiry");
    }
  };

  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      // },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter:   false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "enquiry_number",
          activeType,
          ENQ.DRAFT
        )}`,
      },
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.enquiry_date} />
        ),
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "customer_name",
          activeType,
          ENQ.DRAFT
        )}`,
        cell: ({ row }: { row: any }) => row.original?.customer_name || "-",
      },
      {
        header: "Source of Enquiry",
        accessorKey: "source_of_enquiry",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "source_of_enquiry",
          activeType,
          ENQ.DRAFT
        )}`,
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "agent_name",
          activeType,
          ENQ.DRAFT
        )}`,
        cell: ({ row }: { row: any }) => row.original?.agent_name || "-",
      },
      {
        header: "Reference",
        accessorKey: "reference_number",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "reference_number",
          activeType,
          ENQ.DRAFT
        )}`,
        cell: ({ row }: { row: any }) => row.original?.reference_number || "-",
      },

      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.ENQUIRY?.PRODUCT?.FILTER_DROPDOWN(
          "assigned_members",
          activeType,
          ENQ.ENQUIRY_LOG
        )}`,

        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },

      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `/enquiry/${activeType}/dynamic_dropdown/status`,
      //   cell: ({ row }: { row: any }) => {
      //     return (
      //       <StatusDropdown
      //         currentStatus={row.original.status}
      //         itemId={row.original.id}
      //         onStatusChange={handleStatusChange}
      //         statusOptions={enquiryStatusOptions}
      //         disabled={false}
      //       />
      //     );
      //   },
      // },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        enableSorting: false,

        cell: ({ row }: { row: any }) => {
          const notes = row.original?.notes;

          if (!notes) return "-";

          return (
            <Tooltip
              placement="top"
              title={notes}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 350,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "150px", // ✅ min width
                  maxWidth: "250px", // optional
                  display: "-webkit-box",
                  WebkitLineClamp: 4, // ✅ 3–4 lines
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  cursor: "pointer",
                }}
              >
                {notes}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user.username}
            date={row.original.updated_at}
          />
        ),
      },
    ];

    const actionColumn = {
      header: "Action",
      cell: ({ row }: { row: any }) => {
        const enquiry_id =
          activeType === "product"
            ? row.original.enquiry_product_id
            : row.original.enquiry_service_id;

        const actions = canUpdate ? [
          {
            label: "Edit",
            permission: canUpdate,
            icon: <EditIcon fontSize="small" color="primary" />,
            onClick: () => handleEdit(enquiry_id),
          },
          {
            label: "Delete",
            permission: canUpdate,
            icon: <DeleteOutlineIcon fontSize="small" color="error" />,
            onClick: () => handleDelete(enquiry_id),
            className: "text-danger", // optional, can be removed if not used
          },
        ] : [];

        // return <ActionDropdown actions={actions} />;
        return <DynamicMenuItem row={row.original} menuItems={actions} />;
      },
    };

    return canUpdate ? [...commonColumns, actionColumn] : [...commonColumns];
  };

  return (
    <>

      <MasterListPage
        activeTab="draft"
        title={`${activeType === "product" ? "Sales" : "Service"} Enquiry`}
        addButtonLabel={addButtonLabel}
        addRoute={addRoute}
        columns={getColumns()}
        statusToggleUrl={`/enquiry/${activeType}`}
        activeType={activeType}
        reloadFlag={reload}
        onTagsSearchTabNavigate={onTagsSearchTabNavigate}
        noWrapper={noWrapper}
        forceShowOnTagsSearch={forceShowOnTagsSearch}
      />

    </>
  );
};

export default EnquiryDraftPage;
